import React, { useEffect, useState } from "react";
import { doc, setDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import {fontSizes, rounded, spacing, colors } from "../styles/tokens";

export default function BookmarkImportView({ user, onBack, reloadCallback, preloadedData = null }) {
  const [importedData, setImportedData] = useState(preloadedData || []);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (preloadedData) setImportedData(preloadedData);
  }, [preloadedData]);

  const handleFile = async (e) => {
    if (preloadedData) return;
    const file = e.target.files[0];
    if (!file) return;

    const text = await file.text();
    let parsed = [];
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      alert("Invalid JSON");
      return;
    }

    setImportedData(parsed);
  };

  const toggleSelection = (videoId, sectionId) => {
    setSelected((prev) => {
      const key = `${videoId}|${sectionId}`;
      const copy = { ...prev };
      if (copy[key]) delete copy[key];
      else copy[key] = true;
      return copy;
    });
  };

  const handleImport = async () => {
    const toImport = importedData.filter((s) => selected[`${s.videoId}|${s.id}`]);
    const counts = { videos: new Set(), sections: 0 };

    for (const section of toImport) {
      const { videoId, videoTitle, id: _discard, ...data } = section;
      counts.videos.add(videoId);

      const videoRef = doc(db, "users", user.uid, "videos", videoId);
      await setDoc(videoRef, { title: videoTitle || "Untitled Video" }, { merge: true });

      const sectionRef = doc(collection(videoRef, "bookmarks"));
      await setDoc(sectionRef, { videoId, videoTitle, ...data, uid: user.uid, id: sectionRef.id });

      counts.sections++;
    }

    alert(`Imported ${counts.sections} sections across ${counts.videos.size} videos.`);
    reloadCallback?.();
    onBack();
  };

  const grouped = importedData.reduce((acc, s) => {
    if (!acc[s.videoId]) acc[s.videoId] = { title: s.videoTitle, sections: [] };
    acc[s.videoId].sections.push({ ...s });
    return acc;
  }, {});

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-40" />

      <div
        className={`fixed top-4 right-4 ${colors.surface} border ${rounded.md} shadow-lg max-h-[90vh] overflow-y-auto w-full max-w-xl z-50 ${spacing.md}`}
      >
        <h3 className={`font-semibold mb-4 ${fontSizes.lg}`}>📥 Import Loopy Sections</h3>

        {!importedData.length && !preloadedData && (
          <input type="file" accept="application/json" onChange={handleFile} />
        )}

        {importedData.length > 0 && (
          <div className={`${spacing.gapMd} flex flex-col`}>
            {Object.entries(grouped).map(([vid, data]) => (
              <div key={vid} className={`border ${rounded.sm}`}>
                <div className="bg-gray-100 px-2 py-1 rounded-t-sm font-semibold text-sm">
                  {data.title}
                </div>
                <ul className={`px-2 py-2 ${spacing.gapSm} flex flex-col`}>
                  {data.sections.map((sec) => (
                    <li key={sec.id}>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!selected[`${vid}|${sec.id}`]}
                          onChange={() => toggleSelection(vid, sec.id)}
                        />
                        <span>
                          {sec.title} : {sec.start.toFixed(1)} - {sec.end.toFixed(1)}, {sec.speed.toFixed(2)}x
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className={`flex ${spacing.gapSm} mt-6 border-t pt-4`}>
          <button
            onClick={onBack}
            className={`bg-gray-400 text-white px-4 py-2 flex-1 ${rounded.md} ${fontSizes.sm} hover:bg-gray-500`}
          >
            Back
          </button>
          {importedData.length > 0 && (
            <button
              onClick={handleImport}
              className={`bg-green-600 text-white px-4 py-2 flex-1 ${rounded.md} ${fontSizes.sm} hover:bg-green-700`}
            >
              Import Selected
            </button>
          )}
        </div>
      </div>
    </>
  );
}
